<template>
    <div>
        <div style="margin-bottom: 10px;">
            <h3>E-106 Submission</h3>
            <div style="line-height:1.5em;">
                To request a Section 106 compliance letter for a proposed project from the Wyoming SHPO you will need to fill out the form below.
                 Based on a file search and our review of the project area, we will provide written comments on the potential for the project to affect
                 historic properties and recommendations for further cultural resource investigations. Things you will need to have ready to fill out this form include:
                <br /><br />
                &nbsp;&nbsp;&nbsp;&nbsp;•&nbsp;&nbsp;Name and adequate description of project
                <br />
                &nbsp;&nbsp;&nbsp;&nbsp;•&nbsp;&nbsp;Name of project proponent
                <br />
                &nbsp;&nbsp;&nbsp;&nbsp;•&nbsp;&nbsp;Project location (township, range, section)
                <br />
                &nbsp;&nbsp;&nbsp;&nbsp;•&nbsp;&nbsp;Name of lead federal (or state) agency permitting, authorizing, or funding project
                <br />
                &nbsp;&nbsp;&nbsp;&nbsp;•&nbsp;&nbsp;Current land use and nature of any previous disturbances in project area
                <br />
                &nbsp;&nbsp;&nbsp;&nbsp;•&nbsp;&nbsp;A copy of a USGS 7.5 minute topographic map with the project area outlined or highlighted
                <br />
                &nbsp;&nbsp;&nbsp;&nbsp;•&nbsp;&nbsp;Photos of the project area
                <br />
                &nbsp;&nbsp;&nbsp;&nbsp;•&nbsp;&nbsp;Date(s) of building/structure construction, if applicable
                <br /><br />
                The form below is only for select types of projects and is not a substitute for the normal review and consultation process.
                <br /><br />
                If a cultural resource inventory report has already been prepared for a specific project, it should first be reviewed by the lead permitting
                 agency for the project through WyoTrack. That agency will then send a copy of the report to the SHPO through WyoTrack with their comments and determinations
                 and a request for SHPO review. If the permitting agency has authorized a project proponent or cultural resource consultant to send a copy of the report
                 directly to the SHPO, send a cover letter along with the report that explains the situation, requests SHPO review and comment, and provides information
                 about who should receive the SHPO response letter. All of these things are done through WyoTrack. Please visit
                 <a href="https://wyoshpo.wyo.gov/index.php/programs/cultural-records/wyotrack" target=”_blank”>https://wyoshpo.wyo.gov/index.php/programs/cultural-records/wyotrack</a> if you have
                 questions or need access to WyoTrack.
            </div>
        </div>
        <div class="wrapper">
            <div class="row row-wider">
                <div class="col-md-12" style="min-height: 150px;">
                    <loader v-show="isLoading" />
                    <div v-show="!isLoading" v-if="!isSubmitted"> 
                        <form @submit.prevent="" class="form-horizontal" data-vv-scope="publicE106">
                            <div id="publicE106Project">
                                <h4>Project</h4>
                                <div class="form-group" v-bind:class="{ 'has-error': errors.has('publicE106.projectName') }">
                                    <label class="col-md-3 control-label">Project Name</label>
                                    <div class="col-md-5">
                                        <input name="projectName" type="text" class="form-control" v-model="project.name" placeholder="Project Name" v-validate="'required'" aria-required="true">
                                        <span v-show="errors.has('publicE106.projectName')" class="has-error">Project Name is required</span>
                                    </div>
                                </div>
                                <div class="form-group" v-bind:class="{ 'has-error': errors.has('publicE106.projectTypeId') }">
                                    <label class="col-md-3 control-label">Project Type</label>
                                    <div class="col-md-6">
                                        <select name="projectTypeId" class="form-control" v-model="project.projectTypeId" v-validate="'required'" aria-required="true">
                                            <option :value="null">Select a value</option>
                                            <option v-for="projectType in projectTypeList" :key="projectType.projectTypeId" :value="projectType.projectTypeId">
                                                {{ projectType.description}}
                                            </option>
                                        </select>
                                        <span v-show="errors.has('publicE106.projectTypeId')" class="has-error">Project Type is required</span>
                                    </div>
                                </div>
                                <div class="form-group" v-bind:class="{ 'has-error': errors.has('publicE106.leadAgency') }">
                                    <label class="col-md-3 control-label">Lead Agency</label>
                                    <div class="col-md-8">
                                        <select name="leadAgency" class="form-control" v-model="project.agencyId" v-validate="'required'" aria-required="true">
                                            <option :value="null">Select a value</option>
                                            <option v-for="organization in leadOrganizationList" :key="organization.id" :value="organization.id">{{ organization.description}}</option>
                                        </select>
                                        <span v-show="errors.has('publicE106.leadAgency')" class="has-error">Lead Agency is required</span>
                                        <span v-show="selectedLeadOrganization && selectedLeadOrganization.hasUsers === false && !selectedLeadOrganization.agencyNoReview" class="has-error">
                                            Selected Lead Agency has no Wyotrack users
                                        </span>
                                    </div>
                                </div>
                                <div class="form-group">
                                    <label class="col-md-3 control-label">Agency Number</label>
                                    <div class="col-md-5">
                                        <input name="name" type="text" class="form-control" v-model="project.agencyNumber" placeholder="Agency Number">
                                    </div>
                                </div>
                                <div class="form-group" v-bind:class="{ 'has-error': errors.has('publicE106.client') }">
                                    <label class="col-md-3 control-label">Client</label>
                                    <div class="col-md-8">
                                        <select name="client" class="form-control" v-model="project.clientId" v-validate="'required'" aria-required="true">
                                            <option :value="null">Select a value</option>
                                            <option v-for="organization in organizationList" :key="organization.organizationId" :value="organization.organizationId">
                                                {{ organization.description}}
                                            </option>
                                        </select>
                                        <span v-show="errors.has('publicE106.client')" class="has-error">Client is required</span>
                                    </div>
                                </div>
                                <div class="form-group">
                                    <label class="col-md-3 control-label">Field Org.</label>
                                    <div class="col-md-8">
                                        <select name="fieldOrganizationId" class="form-control" v-model="project.fieldOrganizationId" >
                                            <option :value="null">Select a value</option>
                                            <option v-for="organization in organizationList" :key="organization.organizationId" :value="organization.organizationId">
                                                {{ organization.description}}
                                            </option>
                                        </select>
                                    </div>
                                </div>
                                <div class="form-group">
                                    <label class="col-md-3 control-label">Field Org. Number</label>
                                    <div class="col-md-5">
                                        <input name="fieldOrgNumber" type="text" class="form-control" v-model="project.fieldOrgNumber" placeholder="Field Org. Number">
                                    </div>
                                </div>
                                <div class="form-group">
                                    <label class="col-md-3 control-label">&nbsp;</label>
                                    <div class="col-md-8">
                                        <input type="checkbox" id="IsFederalAgencyImpacted" v-model="project.isFederalAgencyImpacted" :disabled="!isFederalAgencyImpactedCheckboxEnabled">
                                        <label for="IsFederalAgencyImpacted">Is this a federal undertaking?</label>
                                    </div>
                                </div>
                            </div>                      
                            <div id="publicE106Main">
                                <div class="form-group" v-bind:class="{ 'has-error': errors.has('publicE106.projectDescription') }">
                                    <label class="col-md-3 control-label text-label">Project Description</label>
                                    <!-- <div class="col-md-3"><h4 style="margin-bottom: 0px;">Project Description</h4></div> -->
                                    <div class="col-md-9">
                                        <textarea class="form-control" name="projectDescription" rows="2" v-model="complianceDto.projectDescription" v-validate="'required'"/>
                                        <span v-show="errors.has('publicE106.projectDescription')" class="has-error">Project Description is required</span>
                                    </div>
                                </div>
                                <h4>Property Information</h4>
                                <div class="form-group">
                                    <div class="col-md-4">
                                        <button name="addPropertyInfo" class="btn btn-primary" v-on:click="addNewPropertyInfo()">Add Property Information</button>
                                    </div>
                                </div>
                                <div>
                                    <div>
                                        <v-client-table :data="propertyInfoGridSpec.tableData" :columns="propertyInfoGridSpec.columns" :options="propertyInfoGridSpec.options">
                                            <template slot="edit" slot-scope="props">
                                                <div style="text-align: center;">
                                                    <a class="fa fa-edit" style="font-size: 18px;" v-on:click="editPropertyInformation(props.row)"></a>
                                                </div>
                                            </template>
                                            <template slot="delete" slot-scope="props">
                                                <div style="text-align: center;">
                                                    <a class="fa fa-ban" style="font-size: 18px;" v-on:click="deletePropertyInformation(props.row)"></a>
                                                </div>
                                            </template>
                                        </v-client-table>
                                    </div>
                                </div>
                                <h4>PLSS Locations</h4>
                                <div class="form-group">
                                    <div class="col-md-4">
                                        <button name="button3" class="btn btn-primary" v-on:click="showSectionModal()">Add PLSS Locations</button>
                                        <modal title="Add Sections(s)" effect="fade/zoom" :value="sectionModalShow">
                                            <div slot="modal-header" class="modal-header">
                                                <h4 class="modal-title">Add PLSS Locations</h4>
                                            </div>
                                            <div slot="modal-body" class="modal-body">
                                                <plssComponent v-on:savePlss="savePlssE106Compliance" ref="plssComponent" v-bind:showTitle="false">
                                                </plssComponent>
                                            </div>
                                            <div slot="modal-footer" class="modal-footer">
                                                <button type="button" class="btn btn-default" @click="closeSectionModal" style="margin-bottom: 0px;">Exit</button>
                                            </div>
                                        </modal>
                                    </div>
                                </div>
                                <div>
                                    <div v-show="plssLocationGridSpec.tableData.length > 0">
                                        <v-client-table :data="plssLocationGridSpec.tableData" :columns="plssLocationGridSpec.columns" :options="plssLocationGridSpec.options">
                                        <template slot="delete" slot-scope="props">
                                            <div style="text-align: center;">
                                                <a class="fa fa-ban" style="font-size: 18px;" v-on:click="deletePlssLocation(props.row)"></a>
                                            </div>
                                        </template>
                                        </v-client-table>
                                    </div>
                                </div>
                                <h4>Land Information</h4>
                                <div class="form-group" v-bind:class="{ 'has-error': errors.has('publicE106.landUseDescription') }">
                                    <label class="col-md-3 control-label text-label">Description of Current Land Use</label>
                                    <div class="col-md-9">
                                        <textarea class="form-control" name="landUseDescription" rows="6" v-model="complianceDto.currentLandUse" v-validate="'required'"/>
                                        <span v-show="errors.has('publicE106.landUseDescription')" class="has-error">Description of Current Land Use is required</span>
                                    </div>
                                </div>
                                <div class="form-group" v-bind:class="{ 'has-error': errors.has('publicE106.previousDisturbances') }">
                                    <label class="col-md-3 control-label text-label">Nature of Any Previous Disturbances</label>
                                    <div class="col-md-9">
                                        <textarea class="form-control" name="previousDisturbances" rows="6" v-model="complianceDto.previousDisturbances" v-validate="'required'"/>
                                        <span v-show="errors.has('publicE106.previousDisturbances')" class="has-error">Nature of Any Previous Disturbances is required</span>
                                    </div>
                                </div>
                                <modal2 ref="propertyInfoModal">
                                    <div slot="header" class="row">
                                        <h3 class="pull-left">Property Information</h3>
                                        <button class="btn pull-right" v-on:click="$refs['propertyInfoModal'].close()">X</button>
                                    </div>
                                    <div slot="body">
                                        <fieldset>
                                            <div class="form-group">
                                                    <label class="col-md-3 control-label text-label">Street Address</label>
                                                    <div class="col-md-6">
                                                        <input type="text" class="form-control" v-model="selectedPropertyInfo.streetAddress" />
                                                    </div>
                                            </div>
                                            <div class="form-group">
                                                <label class="col-md-3 control-label text-label">City</label>
                                                <div class="col-md-3">
                                                    <input type="text" class="form-control" v-model="selectedPropertyInfo.city" />
                                                </div>
                                                <label class="col-md-1 control-label text-label">County</label>
                                                <div class="col-md-2">
                                                    <input type="text" class="form-control" v-model="selectedPropertyInfo.county" />
                                                </div>
                                            </div>
                                            <div class="form-group">
                                                <label class="col-md-3 control-label text-label">Zip Code</label>
                                                <div class="col-md-2">
                                                    <input type="text" class="form-control" v-model="selectedPropertyInfo.zipCode" />
                                                </div>
                                                <label class="col-md-1 control-label text-label">Lot</label>
                                                <div class="col-md-1">
                                                    <input type="text" class="form-control" v-model="selectedPropertyInfo.lot" />
                                                </div>
                                                <label class="col-md-1 control-label text-label">Block</label>
                                                <div class="col-md-1">
                                                    <input type="text" class="form-control" v-model="selectedPropertyInfo.block" />
                                                </div>
                                            </div>
                                            <div class="form-group">
                                                <label class="col-md-3 control-label text-label">Date of Construction</label>
                                                <div class="col-md-4">
                                                    <input type="text" class="form-control" v-model="selectedPropertyInfo.constructionDate">
                                                </div>
                                            </div>
                                            <div class="form-group">
                                                <label class="col-md-3 control-label text-label">Dates of Additions/Modifications</label>
                                                <div class="col-md-4">
                                                    <input type="text" class="form-control" v-model="selectedPropertyInfo.additionsModificationsDates">
                                                </div>
                                            </div>
                                            <div class="form-group">
                                                <label class="col-md-3 control-label text-label">Proposed Changes and Brief History of the Building</label>
                                                <div class="col-md-9">
                                                    <textarea class="form-control" rows="6" v-model="selectedPropertyInfo.proposedChangesBriefHistory" />
                                                </div>
                                            </div>
                                        </fieldset>
                                    </div>
                                    <div slot="footer">
                                        <div class="col-md-1" >
                                            <button type="button" class="btn btn-primary" v-on:click="savePropertyInformation()" style="margin-bottom: 0px;">Save</button>
                                        </div>
                                    </div>
                                </modal2>
                            </div>
                            <hr class="form-group" style="border:solid 1px #cccccc;" />
                            <div id="publicE106Document">
                                <h4>Documents</h4>
                                <div class="form-group" style="margin-top: 20px;">
                                    <documents :parentId="null" :formDisabled="false" :loadOnMount="false"
                                        :addOnly="true" ref="documents"></documents>
                                </div>
                            </div>
                            <hr class="form-group" style="border:solid 1px #cccccc;" />
                            <div id="publicE106Contact">
                                <h4>Contact</h4>
                                <div class="form-group" v-bind:class="{ 'has-error': errors.has('publicE106.submitterFirstName') }">
                                    <label class="col-md-3 control-label">Submitter First Name</label>
                                    <div class="col-md-5">
                                        <input name="submitterFirstName" type="text" class="form-control" v-model="complianceDto.submitterFirstName" placeholder="Submitter First Name" v-validate="'required'" aria-required="true">
                                        <span v-show="errors.has('publicE106.submitterFirstName')" class="has-error">First Name is required</span>
                                    </div>
                                </div>
                                <div class="form-group" v-bind:class="{ 'has-error': errors.has('publicE106.submitterLastName') }">
                                    <label class="col-md-3 control-label">Submitter Last Name</label>
                                    <div class="col-md-5">
                                        <input name="submitterLastName" type="text" class="form-control" v-model="complianceDto.submitterLastName" placeholder="Submitter Last Name" v-validate="'required'" aria-required="true">
                                        <span v-show="errors.has('publicE106.submitterLastName')" class="has-error">Last Name is required</span>
                                    </div>
                                </div>
                                <div class="form-group" v-bind:class="{ 'has-error': errors.has('publicE106.submitterEmail') }">
                                    <label class="col-md-3 control-label">Submitter Email</label>
                                    <div class="col-md-5">
                                        <input name="submitterEmail" type="text" class="form-control" v-model="complianceDto.submitterEmail" placeholder="user@example.com" v-validate="'required'" aria-required="true">
                                        <span v-show="errors.has('publicE106.submitterEmail')" class="has-error">Email is required</span>
                                    </div>
                                </div>
                                <div class="form-group" v-bind:class="{ 'has-error': errors.has('publicE106.submitterPhone') }">
                                    <label class="col-md-3 control-label">Submitter Phone Number</label>
                                    <div class="col-md-5">
                                        <input name="submitterPhone" type="tel" class="form-control" v-model="complianceDto.submitterPhone" placeholder="123-45-6789" v-validate="'required'" aria-required="true">
                                        <span v-show="errors.has('publicE106.submitterPhone')" class="has-error">Phone Number is required</span>
                                    </div>
                                </div>
                            </div>
                            <hr class="form-group" style="border:solid 1px #cccccc;" />
                            <div class="form-group">
                                <div class="col-md-3"></div>
                                <div class="col-md-8">
                                    <div v-show="errors.any()">
                                        <span class="has-error">Validation issues must be corrected prior to submit</span>
                                    </div>
                                </div>
                            </div>
                            <div class="form-group">
                                <div class="col-md-3"></div>
                                <div class="col-md-8">
                                    <div v-show="validationList.length > 0">
                                        <span class="has-error">Validation issues must be corrected prior to submit</span>
                                        <ul class="has-error">
                                            <li v-for="validationMsg in validationList" :key="validationMsg">{{ validationMsg }}</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            <div class="form-group">
                                <div v-show="submitServerError">
                                    <div class="alert alert-danger" role="alert">
                                        <div style="margin-bottom: 10px;">Errors encountered while submitting your form. Please try again later.</div>
                                    </div>
                                </div>
                            </div>
                            <div class="form-group" id="pe106-submission">
                                <div class="col-md-3">
                                    <h4>Submission</h4>
                                </div>
                                <div class="col-md-3">
                                    <button name="button2" class="btn btn-primary btn-block" style="width:100px;" :disabled="disableSubmit" v-on:click="submitPrep()">Submit</button>
                                </div>
                            </div>
                        </form>
                    </div>
                    <div v-else>
                        <div class="alert alert-warning" role="alert" v-if="submitMediaError">
                            <div style="margin-bottom: 10px;">Your E-106 Submission is received. However, there was an error submitting your uploaded documents.</div>
                            <div><router-link to="/home" tag="a">Return</router-link></div>
                        </div>
                        <div class="alert alert-success" role="alert" v-else>
                            <div style="margin-bottom: 10px;">Your E-106 Submission is received, thank you.</div>
                            <div><router-link to="/home" tag="a">Return</router-link></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import loggingService from '../services/util/loggingService'
//import dateService from '../services/util/dateService'
import projectService from '../services/projectService'
import organizationService from '../services/organizationService'
import projectTypeService from '../services/lookup/projectTypeService'
import e106ComplianceService from '@/services/e106ComplianceService'
import mediaService from '../services/mediaService'

import loader from '../components/Loader.vue'
import modal2 from '../components/Modal.vue'
import { tab, tabs, modal } from 'vue-strap'
import _ from 'lodash/lodash.min.js'
import plssComponent from '../components/PlssComponent.vue'
import documents from '@/components/e106ComplianceDocuments'
import Vue from 'vue'
import { VueReCaptcha } from 'vue-recaptcha-v3'
Vue.use(VueReCaptcha, { siteKey: '6Lf3CdYlAAAAAEQBfbzZ63ub8ha_zalGb7vGaEI-' })

export default {
    components: {
        tabs,
        tab,
        loader,
        modal,
        modal2,
        plssComponent,
        documents
    },
    data: function () {
        return {
            isDebug: false,
            isLoading: false,
            project: {},
            leadOrganizationList: [],
            organizationList: [{description: ''}],
            projectTypeList: [],
            validationList: [],
            projectIdentifierTypeList: projectService.getIdentifierTypeList(),
            selectedLeadOrganization: null,
            isFederalAgencyImpactedCheckboxEnabled: false,
            projectIdentifierList: [],
            selectedClient: {},
            complianceDto: {},
            plssLocationGridSpec: {
                columns: ['meridianDescription', 'township', 'townshipDirection', 'range', 'rangeDirection', 'section', 'delete'],
                tableData: [],
                options: {
                    dateFormat: 'MM/DD/YYYY, HH:mm A',
                    perPage: 10,
                    perPageValues: [10, 25, 50, 100],
                    filterable: false,
                    dateColumns: ['createdDate', 'modifiedDate'],
                    headings: {
                        townshipDirection: 'Township Dir.',
                        rangeDirection: 'Range Dir.',
                        meridianDescription: 'Meridian',
                        delete: 'Delete'
                    },
                    sortable: [],
                    orderBy: {
                        ascending: true,
                        column: 'tempId'
                    }
                },
                submitted: false
            },
            propertyInfoGridSpec: {
                columns: ['edit', 'streetAddress', 'city', 'county', 'zip', 'delete'],
                tableData: [],
                options: {
                    dateFormat: 'MM/DD/YYYY, HH:mm A',
                    perPage: 10,
                    perPageValues: [10, 25, 50, 100],
                    filterable: false,
                    dateColumns: ['createdDate', 'modifiedDate'],
                    headings: {
                        streetAddress: 'Street Address',
                        delete: 'Delete'
                    },
                    sortable: [],
                    orderBy: {
                        ascending: true,
                        column: 'tempId'
                    }
                }
            },
            selectedPropertyInfo: {
                e106complianceId: this.e106complianceId
            },
            sectionModalShow: false,
            sectionModalSaveReady: false,
            pageTempId: 0,
            isSubmitted: false,
            submitServerError: false,
            submitMediaError: false,
            disableSubmit: false
        }
    },
    computed: {
        leadAgencyId: function () {
            return (this.project) ? this.project.agencyId : null
        },
        leadAgencyDescription: function () {
            if (this.project) {
                var leadAgency = this.leadOrganizationList.find(organization => { return organization.id === this.project.agencyId })
                return (leadAgency) ? leadAgency.description : null
            }
        },
        clientDescription: function () {
            if (this.project) {
                var client = this.organizationList.find(organization => { return organization.organizationId === this.project.clientId })
                return (client) ? client.description : null
            }
        }
    },
    mounted () {
        loggingService.log(this.isDebug, 'Project', 'mounted')
        this.isLoading = true
        Promise.all([ this.loadLeadOrganizationList(this), this.loadOrganizations(), this.loadProjectTypeList() ]).then(results => {
            this.project = this.createNewProject()
            this.isLoading = false
        })
    },
    watch: {
        leadAgencyId: function (val) {
            loggingService.log(this.isDebug, 'Project', 'watch.leadAgencyId: ' + val)
            this.selectedLeadOrganization = this.getSelectedLeadOrganizationList(val)
            if (this.selectedLeadOrganization) {
                this.project.isFederalAgencyImpacted = this.selectedLeadOrganization.isFederalAgency
                this.isFederalAgencyImpactedCheckboxEnabled = this.selectedLeadOrganization.is106Agency
            }
        }
    },
    methods: {
        createNewProject () {
            return {
                projectId: 0,
                name: null,
                projectTypeId: null,
                agencyId: null,
                clientId: null,
                fieldOrganizationId: null,
                isFederalAgencyImpacted: false
            }
        },
        loadLeadOrganizationList () {
            loggingService.log(this.isDebug, 'Project', 'loadLeadOrganizationList')
            this.leadOrganizationList = []
            var handle = this
            return organizationService.listLeadAgencyLookup(handle.$http).then(function (response) {
                handle.leadOrganizationList = response.data
            })
        },
        loadOrganizations () {
            loggingService.log(this.isDebug, 'Project', 'loadOrganizations')
            this.organizationList = []
            var handle = this
            return organizationService.listLookup(this.$http).then(function (response) {
                handle.organizationList = response.data
            })
        },
        loadProjectTypeList () {
            loggingService.log(this.isDebug, 'Project', 'loadProjectTypeList')
            this.projectTypeList = []
            var handle = this
            return projectTypeService.listLookup(this.$http).then(function (response) {
                handle.projectTypeList = response.data
                if (handle.isCLGConsultant) {
                    handle.projectTypeList = handle.projectTypeList.filter(x => x.description === 'HPF Programs--Survey')
                }
            })
        },
        getSelectedLeadOrganizationList (organizationId) {
            return _.find(this.leadOrganizationList, function (item) { return item.id === organizationId })
        },
        showSectionModal () {
            this.sectionModalShow = true
        },
        closeSectionModal () {
            this.sectionModalShow = false
        },
        savePlssE106Compliance () {
            var plss = this.$refs['plssComponent'].plssData
            var meridianDes = plss.MeridianId === 1 ? 'Sixth Principal Meridian'
                : (plss.MeridianId === 2 ? 'Wind River Meridian' : '')
            plss.Sections.forEach(s => {
                var plssDto = {
                    tempId: ++this.pageTempId,
                    meridianId: plss.MeridianId,
                    meridianDescription: meridianDes,
                    township: plss.Township,
                    townshipDirection: plss.TownshipDirection,
                    range: plss.Range,
                    rangeDirection: plss.RangeDirection,
                    section: s
                }
                this.plssLocationGridSpec.tableData.push(plssDto)
            })
            this.sectionModalShow = false
        },
        deletePlssLocation (plss) {
            this.plssLocationGridSpec.tableData = this.plssLocationGridSpec.tableData.filter(p => p.tempId !== plss.tempId)
        },
        resetPropertyInfo () {
            this.selectedPropertyInfo = {
            }
        },
        addNewPropertyInfo () {
            this.resetPropertyInfo()
            this.$refs['propertyInfoModal'].open()
        },
        savePropertyInformation () {
            var isNew = false
            if (!this.selectedPropertyInfo.tempId) {
                this.selectedPropertyInfo.tempId = ++this.pageTempId
                isNew = true
            }
            //rebuild table data
            var newTableData = []
            this.propertyInfoGridSpec.tableData.forEach(d => {
                if (d.tempId === this.selectedPropertyInfo.tempId && !isNew) {
                    newTableData.push({...this.selectedPropertyInfo})
                } else {
                    newTableData.push({...d})
                }
            })
            if (isNew) newTableData.push({...this.selectedPropertyInfo})
            this.propertyInfoGridSpec.tableData = newTableData

            this.resetPropertyInfo()
            this.$refs['propertyInfoModal'].close()
        },
        editPropertyInformation (propertyInfo) {
            this.selectedPropertyInfo = {...propertyInfo}
            this.$refs['propertyInfoModal'].open()
        },
        deletePropertyInformation (propertyInfo) {
            this.propertyInfoGridSpec.tableData = this.propertyInfoGridSpec.tableData.filter(o => o.tempId !== propertyInfo.tempId)
        },
        async submitPrep () {
            this.disableSubmit = true
            await this.$recaptchaLoaded()
            this.$recaptcha('submit_e106').then((token) => {
                this.submitForm(token)
            })
        },
        async submitForm (captchaToken) {
            this.$validator.validateAll('publicE106').then(async result => {
                if (result) {
                    this.disableSubmit = false
                    this.isLoading = true
                    this.validationList = []
                    this.submitServerError = false
                    var payload = {
                        e106Compliance: this.complianceDto,
                        project: this.project,
                        propertyInformations: this.propertyInfoGridSpec.tableData,
                        plssLocations: this.plssLocationGridSpec.tableData,
                        uploadFiles: [],
                        captchaToken: captchaToken
                    }
                    //validate media
                    var topoMap = this.$refs.documents.mediaList.find(m => m.mediaTypeId === mediaService.mediaType.E106TopograpicMap)
                    if (!topoMap) {
                        this.isLoading = false
                        this.validationList.push('Topographic Map is required.')
                        return
                    }
                    var mediaDtoPromises = []
                    this.$refs.documents.mediaList.forEach(media => {
                        mediaDtoPromises.push(this.fileToByteArrayMediaDto(media))
                    })
                    Promise.all(mediaDtoPromises).then(mediaResults => {
                        payload.uploadFiles = mediaResults
                        e106ComplianceService.publicSubmit(this.$http, payload).then(async response => {
                            var result = response.data
                            this.isLoading = false
                            this.isSubmitted = result.isValid
                            this.validationList = result.messages
                            //scroll to bottom of page
                            if (!this.isSubmitted) {
                                this.scrollToSubmission()
                            }
                        }).catch(err => {
                            console.log(err)
                            loggingService.log(this.isDebug, 'E-16 Public', 'submit - server error')
                            this.submitServerError = true
                            this.isLoading = false
                            this.scrollToSubmission()
                        })
                    })
                } else {
                    loggingService.log(this.isDebug, 'E-16 Public', 'submit - invalid form')
                    this.disableSubmit = false
                }
            })
        },
        scrollToSubmission () {
            this.$nextTick().then(() => {
                let elmnt = document.getElementById('pe106-submission')
                elmnt.scrollIntoView()
            })
        },
        async fileToByteArrayMediaDto (media) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader()
                reader.readAsBinaryString(media.file)
                reader.onload = () => {
                    var binaryString = reader.result
                    resolve(
                        {
                            mediaTypeId: media.mediaTypeId,
                            mediaSubtypeId: media.mediaSubtypeId,
                            comment: media.comment,
                            filename: media.filename,
                            contentType: media.file.type,
                            fileContent: btoa(binaryString)
                        }
                    )
                }
                reader.onerror = error => reject(error)
            })
        }
    }
}

</script>


<style>

.text-label {
    line-height: 24px !important;
}

.tabContent {
    margin-left: 30px;
}

.sectionHeader {
    overflow: hidden;
    margin-top: 20px;
}

.sectionHeaderLeft {
    float: left;
    font-size: 18px;
}

.sectionHeaderRight {
    float: right;
}

</style>



// WEBPACK FOOTER //
// PublicE106.vue?feb5e4ca